/**
 * Personal AI Insights — Weekly summary recap (P3-S5 Doc 46).
 */
import {
  CalendarRange,
  Loader2,
  MessageSquare,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import * as React from "react";

import { myInsightsApi, type SummaryOut } from "@/api/insights";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useUIStore } from "@/stores/ui";

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function PeriodBadge({ start, end }: { start: string; end: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">
      <CalendarRange className="size-3" />
      {fmtDate(start)} → {fmtDate(end)}
    </span>
  );
}

export function InsightsPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<SummaryOut[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [generating, setGenerating] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await myInsightsApi.list(8);
      setItems(r.items);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tải được insights",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleGenerateNow = async (
    target: "current_week" | "previous_week",
  ): Promise<void> => {
    setGenerating(true);
    try {
      const r = await myInsightsApi.generateNow(target);
      if (r.ok) {
        pushToast({
          variant: "success",
          title: "Đã tạo summary",
          description: target === "current_week" ? "Tuần hiện tại" : "Tuần trước",
        });
        await refresh();
      } else {
        pushToast({
          variant: "destructive",
          title: "Không tạo được",
          description: r.error ?? "Lỗi không rõ",
        });
      }
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tạo được",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="container max-w-4xl py-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="size-6 text-primary" /> Insights — Tổng kết tuần
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            AI tự động tổng kết hoạt động của bạn mỗi tuần. Doc 46.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={generating}
            onClick={() => void handleGenerateNow("previous_week")}
          >
            {generating ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
            Tuần trước
          </Button>
          <Button
            size="sm"
            disabled={generating}
            onClick={() => void handleGenerateNow("current_week")}
          >
            {generating ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
            Tuần này
          </Button>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-6 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Đang tải insights…
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center space-y-2">
            <Sparkles className="size-8 mx-auto text-muted-foreground" />
            <p className="font-medium">Chưa có summary nào</p>
            <p className="text-sm text-muted-foreground">
              AI sẽ tự động tạo summary mỗi Chủ Nhật. Hoặc bấm <strong>Tuần này</strong>{" "}
              ở trên để tạo ngay.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((it) => (
            <Card key={it.id}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <PeriodBadge start={it.period_start} end={it.period_end} />
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <MessageSquare className="size-3" />
                      {it.message_count} msg · {it.conversation_count} convs
                    </span>
                    {it.llm_provider ? (
                      <span className="inline-flex items-center gap-1">
                        <TrendingUp className="size-3" />
                        {it.llm_provider}
                        {it.llm_model ? `:${it.llm_model}` : ""}
                      </span>
                    ) : null}
                  </div>
                </div>
                <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">
                  {it.summary_text}
                </pre>
                <div className="text-[10px] text-muted-foreground">
                  Generated{" "}
                  {new Date(it.generated_at).toLocaleString("vi-VN")}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
